import './styles.css';

import { alpha, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  Map,
  Overlay,
  View
} from 'ol';
import { defaults, FullScreen } from 'ol/control';
import { Extent, getCenter } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';
import { Geometry } from 'ol/geom';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { useGeographic } from 'ol/proj';
import { OSM, Vector as VectorSource } from 'ol/source';
import React, {
  forwardRef, memo,
  useEffect, useImperativeHandle, useRef, useState
} from 'react';

import { createFeatureStyles } from './utils/featureStyle';

const useStyles = makeStyles((theme: any) => ({
  tooltip: {
    position: 'relative',
    padding: '5px 10px',
    borderRadius: 5,
    background: alpha(theme.palette.common.black, 0.6),
    color: theme.palette.common.white,
    opacity: 1,
    whiteSpace: 'nowrap',
    fontSize: 'small',
    boxSizing: 'border-box',
    '&::after': {
      content: '" "',
      position: 'absolute',
      bottom: '100%',
      left: '45%',
      borderWidth: 5,
      borderStyle: 'solid',
      borderColor: `transparent transparent ${alpha(theme.palette.common.black, 0.6)} transparent`,
    },
  },
}));

export type OlMapRefType = {
	selectFeatureById: ((id: string) => void) | null
}

export type FeaturePoint = {
	id: string,
	type: string,
	geometry: {
		coordinates: number[] | number[][][][],
		type: string
	},
	properties?: {
		label: string
	}
}

type OlMapPropTypes = {
	features: FeaturePoint[],
	initZoom?: number,
	focusZoom?: number,
	onFeatureSelect?: (id: string) => void,
	fitZoomOut?: number,
	className?: string
}

const OlMapComponent = forwardRef<OlMapRefType, OlMapPropTypes>(
  (props, ref) => {
    const {
      features,
      initZoom = 5.5,
      focusZoom = 10,
      onFeatureSelect,
      fitZoomOut = 0,
      className
    } = props;
    const htmlMapRef = useRef(null);
    const htmlTooltipRef = useRef<HTMLDivElement>(null);
    const [selectFeatureById, setSelectFeatureById] = useState<((id: string) => (void)) | null>(null);
    useGeographic();
    useImperativeHandle(ref, () => ({ selectFeatureById }), [selectFeatureById]);
    const theme = useTheme();
    const classes = useStyles();
    const isDark = theme.palette.mode === 'dark';
    const featureStylesRef = useRef<ReturnType<typeof createFeatureStyles>>();
    const mapRef = useRef<Map>();
    const vectorLayerRef = useRef<VectorLayer<VectorSource<Feature<Geometry>>>>();
    const overlayRef = useRef<Overlay>();

    useEffect(() => {
      featureStylesRef.current = createFeatureStyles(theme);
      vectorLayerRef.current?.changed();
    }, [theme]);

    useEffect(() => {
      vectorLayerRef.current = new VectorLayer({
        style(feature) {
          return featureStylesRef.current![feature.getGeometry()!.getType()];
        }
      });

      mapRef.current = new Map({
        controls: defaults({
          attribution: false,
          rotate: false,
          zoomOptions: { className: 'ol-zoom-button-container' }
        }).extend([new FullScreen({ className: 'ol-full-screen-button' })]),
        layers: [
          new TileLayer({ source: new OSM(), className: 'ol-basemap' }),
          vectorLayerRef.current
        ],
        view: new View({
          projection: 'EPSG:4326',
          center: [-98.70607734127819, 32.4021682084251],
          zoom: initZoom
        }),
        target: htmlMapRef.current!
      });

      overlayRef.current = new Overlay({ element: htmlTooltipRef.current!, offset: [10, 20], positioning: 'top-center' });
      mapRef.current.addOverlay(overlayRef.current);

      setSelectFeatureById(
        () => (id: string) => {
          const featureList = vectorLayerRef.current!.getSource()?.getFeatures();
          const feature = featureList?.find((x) => x.getId() === id);
          let coordinates: Extent | undefined = [-98.70607734127819, 32.4021682084251];
          if (feature) {
            coordinates = feature.getGeometry()?.getExtent();

            if (feature.getGeometry()?.getType() === "MultiPolygon") {
              coordinates = getCenter(feature.getGeometry()?.getExtent()!);
            }

            const featureLabel: string = feature.get('label');
            if (featureLabel && featureLabel.length > 0) {
							htmlTooltipRef.current!.innerText = featureLabel;
							overlayRef.current!.setPosition(feature.getGeometry()?.getExtent());
            }
          } else {
						mapRef.current!.getView().setZoom(initZoom);
          }

					htmlTooltipRef.current!.style.display = feature ? 'block' : 'none';
					mapRef.current!.getView().setCenter(coordinates);

					if (feature) {
						mapRef.current!.getView().fit(feature?.getGeometry()?.getExtent()!, {
						  padding: [50, 50, 50, 50],
						  duration: 1000,
						  maxZoom: 18
						});
					}
        }
      );
      // Map initialisation is mount-only; re-running on prop changes would rebuild the map.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (vectorLayerRef.current && mapRef.current) {
        const validFeatures = features.filter((feature) => Object.keys(feature.geometry).length);
        const vectorSource = new VectorSource({
          features: new GeoJSON().readFeatures({
            type: 'FeatureCollection',
            features: validFeatures
          })
        });
        vectorLayerRef.current.setSource(vectorSource);
        vectorLayerRef.current.changed();

        // Fit map view to extent of all valid features with padding
        if (validFeatures.length > 0) {
          const allFeatures = vectorSource.getFeatures();
          if (allFeatures.length > 0) {
            let extent = allFeatures[0].getGeometry()?.getExtent();
            if (extent) {
              for (let i = 1; i < allFeatures.length; i += 1) {
                const featureExtent = allFeatures[i].getGeometry()?.getExtent();
                if (featureExtent) {
                  extent = [
                    Math.min(extent[0], featureExtent[0]),
                    Math.min(extent[1], featureExtent[1]),
                    Math.max(extent[2], featureExtent[2]),
                    Math.max(extent[3], featureExtent[3])
                  ];
                }
              }
              const view = mapRef.current.getView();
              mapRef.current.updateSize();
              view.fit(extent, {
                padding: [50, 50, 50, 50],
                duration: fitZoomOut > 0 ? 0 : 1000,
                maxZoom: 18
              });
              if (fitZoomOut > 0) {
                const currentZoom = view.getZoom() ?? initZoom;
                view.setZoom(Math.max(0, currentZoom - fitZoomOut));
              }
              mapRef.current.renderSync();
            }
          }
        }

        mapRef.current.on('click', (e) => {
          const feature = mapRef.current!.getFeaturesAtPixel(e.pixel)[0];
          if (!feature) return;
					mapRef.current!.getView().setCenter(feature.getGeometry()?.getExtent());
					mapRef.current!.getView().setZoom(focusZoom);
					if (onFeatureSelect && feature.getId()) {
					  onFeatureSelect(feature!.getId()!.toString());
					}
					const featureLabel: string = feature.get('label');
					if (featureLabel && featureLabel.length > 0) {
						htmlTooltipRef.current!.style.display = 'block';
						htmlTooltipRef.current!.innerText = featureLabel;
						overlayRef.current!.setPosition(feature.getGeometry()?.getExtent());
					}
        });

        mapRef.current.on('pointermove', (e) => {
          const feature = mapRef.current!.getFeaturesAtPixel(e.pixel)[0];
          if (feature) {
            const featureLabel: string = feature.get('label');
            if (featureLabel && featureLabel.length > 0) {
							htmlTooltipRef.current!.innerText = featureLabel;
							overlayRef.current!.setPosition(feature.getGeometry()?.getExtent());
            }
          }
					htmlTooltipRef.current!.style.display = feature ? 'block' : 'none';
        });
      }
      // Zoom props are read inside the click handler; re-running would duplicate map listeners.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [features, onFeatureSelect, fitZoomOut]);

    const mapClassName = ['map', isDark && 'ol-dark', className].filter(Boolean).join(' ');

    return (
      <div ref={htmlMapRef} id="map" className={mapClassName} style={{ height: '100%', width: '100%' }}>
        <div ref={htmlTooltipRef} className={classes.tooltip} />
      </div>
    );
  }
);

export const OlMap = memo(OlMapComponent, (prevProps, nextProps) => {
  if (prevProps.features.length !== nextProps.features.length) return false;

  if (!prevProps.features
    .every(prevFeature => nextProps.features.find((nextFeature) => nextFeature.id === prevFeature.id))) {
    return false;
  }

  return true;
});
